import { useEffect, useState } from "react";
import adminApi, { fmtDate } from "../../lib/adminApi";
import { fmtUsd, formatErr } from "../../lib/api";
import { MagnifyingGlass, Check, X, Copy } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";

const TABS = [
  { key: "PENDING", label: "В ожидании" },
  { key: "COMPLETED", label: "Одобрены" },
  { key: "REJECTED", label: "Отклонены" },
  { key: "all", label: "Все" },
];

const STATUS_STYLE = {
  PENDING: "bg-[#E3B341]/15 text-[#E3B341]",
  COMPLETED: "bg-[#0ECB81]/15 text-[#0ECB81]",
  REJECTED: "bg-[#F6465D]/15 text-[#F6465D]",
  CANCELLED: "bg-white/5 text-[#6B7280]",
};

export default function AdminWithdrawals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("PENDING");
  const [q, setQ] = useState("");
  const [rejecting, setRejecting] = useState(null);

  const load = () => adminApi.get("/admin/withdrawals", { params: { status, q } })
    .then((r) => setItems(r.data)).catch((e) => toast.error(formatErr(e)));

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  const decide = async (item, approve, note = "") => {
    try {
      await adminApi.post(`/admin/withdrawals/${item.id}/decide`, { approve, note });
      toast.success(approve ? "Вывод одобрен" : "Вывод отклонён (средства возвращены)");
      setRejecting(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="admin-withdrawals">
      <h2 className="text-lg font-semibold text-white mb-4">Заявки на вывод</h2>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex gap-1 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setStatus(t.key)}
              data-testid={`withdrawals-tab-${t.key}`}
              className={`px-3 h-10 rounded-lg text-xs font-medium transition-colors ${status === t.key ? "bg-[#E3B341]/15 text-[#E3B341] ring-1 ring-inset ring-[#E3B341]/30" : "bg-[#111827] text-[#6B7280] hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по email, имени, адресу…"
            className="h-10 pl-9 bg-[#111827] border-[#1F2937] text-white rounded-lg" data-testid="withdrawals-search" />
        </div>
      </div>

      <div className="space-y-2">
        {items.map((w) => (
          <div key={w.id} className="bg-[#111827] border border-[#1F2937] rounded-xl p-4" data-testid={`withdrawal-${w.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="mono text-lg font-semibold text-white">${fmtUsd(w.amount)}</span>
                  <span className="text-xs text-[#9CA3AF]">{w.network}</span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full ${STATUS_STYLE[w.status] || "bg-white/5 text-[#6B7280]"}`}>{w.status}</span>
                </div>
                <p className="text-xs text-[#9CA3AF] mt-1">{w.user_name || "—"} · {w.user_email || w.user_id}</p>
                <button onClick={() => { navigator.clipboard?.writeText(w.address); toast.success("Адрес скопирован"); }}
                  className="mt-1 flex items-center gap-1 text-[11px] text-[#6B7280] hover:text-white break-all text-left" data-testid={`withdrawal-address-${w.id}`}>
                  <Copy size={12} className="shrink-0" /> <span className="mono break-all">{w.address}</span>
                </button>
                <p className="text-[10px] text-[#4B5563] mt-1">{fmtDate(w.created_at)}</p>
                {w.admin_note && <p className="text-[11px] text-[#9CA3AF] mt-1">Заметка: {w.admin_note}</p>}
              </div>
              {w.status === "PENDING" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(w, true)}
                    className="h-9 bg-[#0ECB81] hover:bg-[#0bb673] text-[#0A0E17] font-semibold" data-testid={`withdrawal-approve-${w.id}`}>
                    <Check size={16} className="mr-1" /> Одобрить
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRejecting(w)}
                    className="h-9 text-[#F6465D] hover:bg-[#F6465D]/10" data-testid={`withdrawal-reject-${w.id}`}>
                    <X size={16} className="mr-1" /> Отклонить
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-[#6B7280] py-8">Нет заявок</div>}
      </div>

      <RejectDialog item={rejecting} onClose={() => setRejecting(null)} onConfirm={(note) => decide(rejecting, false, note)} />
    </div>
  );
}

function RejectDialog({ item, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [item]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-white" data-testid="withdrawal-reject-dialog">
        <DialogHeader><DialogTitle>Отклонить вывод</DialogTitle></DialogHeader>
        <DialogDescription className="text-[#9CA3AF] text-sm">Средства будут возвращены на баланс трейдера. Можно указать причину.</DialogDescription>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Причина отклонения (опц.)"
          className="bg-[#0A0E17] border-[#1F2937] text-white" data-testid="withdrawal-reject-note" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#9CA3AF]">Отмена</Button>
          <Button onClick={() => onConfirm(note)} className="bg-[#F6465D] hover:bg-[#d93a4f] text-white" data-testid="withdrawal-reject-confirm">
            Отклонить и вернуть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
